import { Button } from "components/buttons/Button"
import { ReactNode, useEffect, useState } from "react"
import CustomModal from "./modals/CustomModal"
import { Skeleton } from "./Skeleton"
import { Link } from "react-router-dom"
import { H2 } from "components/typography/H2"

interface Props {
  url: string
  btnClassName?: string
  documentName?: ReactNode
}

const getFileFormat = (url: string) => {
  const extension = url?.split(".").pop()
  switch (extension) {
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "image"
    case "pdf":
      return "pdf"
    case "docx":
    case "xlsx":
      return "office"
    default:
      return "unsupported"
  }
}

const AllDocPreviewer = ({ url, btnClassName, documentName }: Props) => {
  const [showPreview, setShowPreview] = useState(false)

  const fileType = getFileFormat(url)
  const fileName = url?.split("/").pop()

  return (
    <div>
      <CustomModal
        destroyOnClose
        open={showPreview}
        onCancel={() => {
          setShowPreview(false)
        }}
        title={documentName ? `Preview ${documentName}` : "Preview Document"}
        footer={
          <div className="flex justify-between items-center gap-4">
            <a
              href={url}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm !text-primary500 font-circular"
            >
              Download Document
            </a>
            <Button
              color="neutral"
              onClick={() => {
                setShowPreview(false)
              }}
            >
              Close
            </Button>
          </div>
        }
        width={fileType === "image" ? 600 : 800}
      >
        <div className="max-h-[800px] overflow-auto">
          <H2 className="text-padeBlack font-circular text-base">{fileName}</H2>
          <IframePreview
            url={url}
            fileType={fileType}
          />
        </div>
      </CustomModal>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          setShowPreview(true)
        }}
        className={`${btnClassName} text-sm text-primary500 font-circular`}
      >
        {documentName ?? "Preview Document"}
      </button>
    </div>
  )
}

export default AllDocPreviewer

// After this long the placeholder admits the wait and points at the download link.
const SLOW_PREVIEW_MS = 8000

export const IframePreview = ({
  url,
  fileType,
}: {
  url: string
  fileType: string
}) => {
  // The file can take several seconds to arrive, and a bare frame stays blank
  // until it does, so a placeholder covers it until the frame reports a load.
  const [loading, setLoading] = useState(true)
  const [slow, setSlow] = useState(false)

  useEffect(() => {
    setLoading(true)
    setSlow(false)
    const timer = setTimeout(() => setSlow(true), SLOW_PREVIEW_MS)
    return () => clearTimeout(timer)
  }, [url])

  const handleLoaded = () => setLoading(false)

  const placeholder = loading && (
    <div
      className="absolute inset-0 flex flex-col gap-4 bg-white p-6"
      aria-busy="true"
    >
      <p className="text-sm text-padeSubtext font-avenir">
        {slow
          ? "This is taking longer than usual. Large files can take a while to load, or you can download it instead."
          : "Loading preview…"}
      </p>
      <Skeleton
        width="45%"
        height="20px"
      />
      <div className="flex flex-col gap-3">
        {[92, 85, 96, 70, 88, 60].map((w, i) => (
          <Skeleton
            key={i}
            width={`${w}%`}
          />
        ))}
      </div>
      <Skeleton
        variant="block"
        height="160px"
      />
    </div>
  )

  const renderIframe = (src: string) => (
    <div className="relative h-[600px]">
      <iframe
        src={src}
        title="file preview"
        width="100%"
        height="600px"
        style={{ border: "none", visibility: loading ? "hidden" : "visible" }}
        onLoad={handleLoaded}
      />
      {placeholder}
    </div>
  )

  switch (fileType) {
    case "image":
      return (
        <div className={`relative ${loading ? "min-h-[400px]" : ""}`}>
          <img
            src={url}
            alt="Preview"
            style={{
              maxWidth: "100%",
              height: "auto",
              visibility: loading ? "hidden" : "visible",
            }}
            onLoad={handleLoaded}
            onError={handleLoaded}
          />
          {placeholder}
        </div>
      )
    case "pdf":
      return renderIframe(`${url}#toolbar=0&navpanes=0&scrollbar=0`)
    case "office":
      return renderIframe(
        `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          url
        )}&wdAllowInteractivity=False&wdReadOnly=1`
      )
    default:
      return <p>Unsupported file type</p>
  }
}

export const ImagePreviewer = ({
  imageUrl,
  name,
  btnClassName,
  children,
}: {
  imageUrl: string
  name: string
  btnClassName?: string
  children?: ReactNode
}) => {
  const [showPreview, setShowPreview] = useState(false)

  return (
    <>
      <CustomModal
        destroyOnClose
        open={showPreview}
        onCancel={() => {
          setShowPreview(false)
        }}
        title={`Preview ${name}`}
        footer={
          <div className="flex justify-between items-center gap-4">
            <a
              href={imageUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm !text-primary500 font-circular"
            >
              Download Image
            </a>
            <Button
              color="neutral"
              onClick={() => {
                setShowPreview(false)
              }}
            >
              Close
            </Button>
          </div>
        }
        width={600}
      >
        <div className="max-h-[800px] overflow-auto">
          <img
            src={imageUrl}
            alt={name}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </div>
      </CustomModal>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          setShowPreview(true)
        }}
        className={`${btnClassName} text-sm text-primary500 font-circular`}
      >
        {children ?? name}
      </button>
    </>
  )
}
